import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface BillingProfile {
  businessName: string;
  billingEmail: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  taxId: string;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface Invoice {
  id: string;
  date: string;
  invoiceNumber: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  downloadUrl?: string;
}

export interface OrgBudgetConfig {
  tokenCap?: number;
  period?: string;
  alertThresholdPct?: number;
}

export interface BillingBudgetResponse {
  organisation: {
    orgId: string;
    budgetConfig?: OrgBudgetConfig | null;
  };
  applications: Array<{
    applicationId: string;
    name: string;
    budgetConfig?: OrgBudgetConfig | null;
  }>;
}

export interface UsageChargesResponse {
  from: string;
  to: string;
  tokensTotal: number;
  currency: string;
  ratePerMtokUsd?: number | null;
  amountUsd?: number | null;
  rateCardVersion?: string | null;
  message?: string;
}

export const billingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBillingProfile: builder.query<BillingProfile, void>({
      query: () => '/portal/billing/profile',
      transformResponse: (response: unknown) => normalizeKeys<BillingProfile>(response),
      providesTags: ['Billing'],
    }),

    updateBillingProfile: builder.mutation<BillingProfile, Partial<BillingProfile>>({
      query: (body) => ({
        url: '/portal/billing/profile',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Billing'],
    }),

    getPaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => '/portal/billing/payment-methods',
      transformResponse: (response: unknown) => normalizeKeys<PaymentMethod[]>(response),
    }),

    getInvoices: builder.query<{ invoices: Invoice[] }, void>({
      query: () => '/portal/billing/invoices',
      transformResponse: (response: unknown) =>
        normalizeKeys<{ invoices: Invoice[] }>(response),
      providesTags: ['Invoice'],
    }),

    getBillingBudget: builder.query<BillingBudgetResponse, void>({
      query: () => '/portal/billing/budget',
      transformResponse: (response: unknown) =>
        normalizeKeys<BillingBudgetResponse>(response),
      providesTags: ['Billing'],
    }),

    updateBillingBudget: builder.mutation<
      BillingBudgetResponse,
      { budgetConfig: OrgBudgetConfig }
    >({
      query: (body) => ({
        url: '/portal/billing/budget',
        method: 'PATCH',
        body: {
          budget_config: {
            token_cap: body.budgetConfig.tokenCap,
            period: body.budgetConfig.period || 'monthly',
            alert_threshold_pct: body.budgetConfig.alertThresholdPct,
          },
        },
      }),
      invalidatesTags: ['Billing', 'Analytics'],
    }),

    getUsageCharges: builder.query<
      UsageChargesResponse,
      { from?: string; to?: string } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && 'from' in args && args.from) params.set('from', args.from);
        if (args && 'to' in args && args.to) params.set('to', args.to);
        const qs = params.toString();
        return `/portal/billing/usage-charges${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: unknown) =>
        normalizeKeys<UsageChargesResponse>(response),
      providesTags: ['Billing'],
    }),
  }),
});

export const {
  useGetBillingProfileQuery,
  useUpdateBillingProfileMutation,
  useGetPaymentMethodsQuery,
  useGetInvoicesQuery,
  useGetBillingBudgetQuery,
  useUpdateBillingBudgetMutation,
  useGetUsageChargesQuery,
} = billingApi;
